import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { IconButton, Grid } from '@mui/material';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Colors } from '../../themes';

interface CarouselProps {
  items: React.ReactNode[];
  itemWidth: number;
  maxVisibleItems?: number;
}

export const Carousel: React.FC<CarouselProps> = ({ items, itemWidth, maxVisibleItems }) => {
  const [startIndex, setStartIndex] = useState(0);
  const [visibleItemsCount, setVisibleItemsCount] = useState(1);
  const [currentGap, setCurrentGap] = useState(16);
  const carouselRef = useRef<HTMLDivElement>(null);

  const CHEVRON_SPACE = 80;
  const TOTAL_CHEVRON_SPACE = 2 * CHEVRON_SPACE;
  const MIN_GAP = 8;
  const BASE_GAP = 16;
  const MAX_GAP = 32;

  const calculateLayout = useCallback(() => {
    if (!carouselRef.current || items.length === 0) return;

    const containerWidth = carouselRef.current.offsetWidth;
    const availableWidth = containerWidth - TOTAL_CHEVRON_SPACE;

    if (availableWidth < itemWidth) {
      setVisibleItemsCount(1);
      setCurrentGap(MIN_GAP);
      return;
    }

    const scaleFactor = Math.min(2, availableWidth / 600);
    const gap = Math.max(MIN_GAP, Math.min(MAX_GAP, BASE_GAP * scaleFactor));

    const maxItems = Math.floor((availableWidth + gap) / (itemWidth + gap));
    const finalCount = Math.min(maxItems, items.length);

    const limitedCount = maxVisibleItems ? Math.min(finalCount, maxVisibleItems) : finalCount;

    setVisibleItemsCount(Math.max(1, limitedCount));
    setCurrentGap(Math.round(gap));
  }, [items.length, itemWidth, TOTAL_CHEVRON_SPACE, maxVisibleItems]);

  const visibleItems = useMemo(() => {
    if (visibleItemsCount >= items.length) return items;

    return Array.from({ length: visibleItemsCount }, (_, i) => {
      const itemIndex = (startIndex + i) % items.length;
      return items[itemIndex];
    });
  }, [items, startIndex, visibleItemsCount]);

  const canNavigate = items.length > visibleItemsCount;

  const handleNext = useCallback(() => {
    if (canNavigate) {
      setStartIndex((prev) => (prev + 1) % items.length);
    }
  }, [canNavigate, items.length]);

  const handlePrevious = useCallback(() => {
    if (canNavigate) {
      setStartIndex((prev) => (prev - 1 + items.length) % items.length);
    }
  }, [canNavigate, items.length]);

  useEffect(() => {
    calculateLayout();

    const resizeObserver = new ResizeObserver(calculateLayout);
    if (carouselRef.current) {
      resizeObserver.observe(carouselRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [calculateLayout]);

  return (
    <Grid
      container
      flexWrap='nowrap'
      ref={carouselRef}
      alignItems='center'
      justifyContent='center'
      style={{ width: '100%', maxWidth: '100%' }}>
      <IconButton
        onClick={handlePrevious}
        aria-label='Previous'
        disabled={!canNavigate}
        style={{
          marginRight: '50px',
          flexShrink: 0,
          opacity: canNavigate ? 1 : 0.3,
        }}>
        <ChevronLeft fontSize='large' style={{ fill: Colors.primaryBase }} />
      </IconButton>

      <Grid
        style={{
          display: 'flex',
          gap: `${currentGap}px`,
          alignItems: 'center',
          flexShrink: 0,
          overflow: 'hidden',
        }}>
        {visibleItems.map((item, i) => (
          <Grid key={`${startIndex}-${i}`} style={{ width: itemWidth, flexShrink: 0 }}>
            {item}
          </Grid>
        ))}
      </Grid>

      <IconButton
        onClick={handleNext}
        aria-label='Next'
        disabled={!canNavigate}
        style={{
          marginLeft: '50px',
          flexShrink: 0,
          opacity: canNavigate ? 1 : 0.3,
        }}>
        <ChevronRight fontSize='large' style={{ fill: Colors.primaryBase }} />
      </IconButton>
    </Grid>
  );
};
